import { Toaster } from '@mdvip/shared/components/ui/toaster';
import { IdleTimeoutManager } from '@mdvip/shared/features/idle-timeout/IdleTimeoutManager';
import { RecipeDetailPage } from '@mdvip/shared/features/recipes/RecipeDetailPage';
import { RecipesPage } from '@mdvip/shared/features/recipes/RecipesPage';
import { WellnessArticlesPage } from '@mdvip/shared/features/wellness-articles/WellnessArticlesPage';
import { AuthCallbackPage } from '@mdvip/shared/pages/AuthCallbackPage';
import { LoginPage } from '@mdvip/shared/pages/LoginPage';
import { NotFoundPage } from '@mdvip/shared/pages/NotFoundPage';
import { ApiPassthrough } from '@mdvip/shared/system/ApiPassthrough';
import { DeepLinkAuthHandler } from '@mdvip/shared/system/DeepLinkAuthHandler';
import { getHealthUrl } from '@mdvip/shared/types/config';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { OfflineIndicator } from './components/shared/OfflineIndicator';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { WellbeingSubnav } from './components/WellbeingSubnav';
import { useConfig } from './contexts/ConfigContext';
import { ArticlesPage } from './pages/ArticlesPage';
import { BookmarksPage } from './pages/BookmarksPage';
import { ContactRoute } from './pages/ContactRoute';
import { DashboardPage } from './pages/DashboardPage';
import { FitnessPage } from './pages/FitnessPage';
import { MealPlansPage } from './pages/MealPlansPage';
import { NutritionPage } from './pages/NutritionPage';
import { PaymentsPage } from './pages/payments/PaymentsPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { ResourcesPage } from './pages/ResourcesPage';

function App() {
  const config = useConfig();

  return (
    <>
      <IdleTimeoutManager
        idleTimeoutMs={config.IDLE_TIMEOUT_MS}
        warningDurationMs={config.WARNING_DURATION_MS}
        tokenRefreshThreshold={config.TOKEN_REFRESH_THRESHOLD}
      />
      <BrowserRouter>
        <DeepLinkAuthHandler />
        <Routes>
          {/* Front Door routes /api/* to the frontline APIM; the SPA must never handle it. */}
          <Route path="/api/*" element={<ApiPassthrough fallback={<NotFoundPage />} />} />
          <Route path="/login" element={<LoginPage healthUrl={getHealthUrl(config)} postLoginRedirect="/" />} />
          <Route path="/auth/callback" element={<AuthCallbackPage postLoginRedirect="/" />} />
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/nutrition" element={<NutritionPage />} />
            <Route
              path="/resources/nutrition/recipes"
              element={
                <ProtectedRoute can={['read', 'NutritionRecipe']}>
                  <RecipesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resources/nutrition/recipes/:id"
              element={
                <ProtectedRoute can={['read', 'NutritionRecipe']}>
                  <RecipeDetailPage />
                </ProtectedRoute>
              }
            />
            <Route path="/resources/nutrition/articles" element={<ArticlesPage />} />
            <Route path="/resources/nutrition/meal-plans" element={<MealPlansPage />} />
            <Route
              path="/resources/wellness"
              element={
                <ProtectedRoute can={['read', 'WellnessArticle']}>
                  <WellnessArticlesPage above={<WellbeingSubnav active="wellness" className="pt-1" />} />
                </ProtectedRoute>
              }
            />
            <Route path="/resources/fitness" element={<FitnessPage />} />
            <Route
              path="/payments"
              element={
                <ProtectedRoute can={['read', 'Payment']}>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/bookmarks" element={<BookmarksPage />} />
            <Route path="/contact" element={<ContactRoute />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <Toaster />
        <OfflineIndicator />
      </BrowserRouter>
    </>
  );
}

export default App;
